/* The PCIS dashboard's single filter rail.

   One rail drives all five tabs, because every /health/* endpoint takes the same
   filter set. Fields edit a local DRAFT; nothing refetches until "Apply" — with
   nine filters and eight endpoints behind them, live-updating on every keystroke
   would fire dozens of requests for a filter combination the user never wanted.

   Range presets are the primary control (leadership picks "Last 7 days", not two
   dates); the picker is there for the custom case. `from`/`to` go to the API as
   `yyyy-MM-ddTHH:mm:ss`, matching PcisHealthFilterBar. */
import React, {useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Button, Stack, Switch, Typography} from '@mui/material'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined'
import {endOfDay, format, isValid, startOfDay, startOfMonth, subDays, subHours} from 'date-fns'
import DateRangePicker from '../../../../components/DateRangePicker'
import {SearchSelect} from '../../components'

const API_FMT = "yyyy-MM-dd'T'HH:mm:ss"
const toApi = (d) => (d && isValid(d) ? format(d, API_FMT) : '')
const parse = (s) => {
  if (!s) return null
  const d = new Date(s)
  return isValid(d) ? d : null
}

/* Preset → window. `custom` is the escape hatch the date picker switches us to. */
export const PRESETS = [
  {key: 'today', label: 'Today', range: () => ({from: startOfDay(new Date()), to: endOfDay(new Date())})},
  {key: '24h', label: 'Last 24h', range: () => ({from: subHours(new Date(), 24), to: new Date()})},
  {key: '7d', label: 'Last 7 days', range: () => ({from: startOfDay(subDays(new Date(), 6)), to: endOfDay(new Date())})},
  {key: '30d', label: 'Last 30 days', range: () => ({from: startOfDay(subDays(new Date(), 29)), to: endOfDay(new Date())})},
  {key: 'mtd', label: 'Month to date', range: () => ({from: startOfMonth(new Date()), to: endOfDay(new Date())})},
]

const DEFAULT_PRESET = '24h'

const presetWindow = (key) => {
  const p = PRESETS.find((x) => x.key === key) || PRESETS.find((x) => x.key === DEFAULT_PRESET)
  const {from, to} = p.range()
  return {from: toApi(from), to: toApi(to)}
}

/* Default view: last 24 hours, everything else unfiltered. Every aggregate on the page is windowed,
   so the landing range sets how long the first paint takes — and the Proship order count behind the
   routing cards is the slow one, taking seconds over 30 days. A day still shows an hourly trend and
   lands quickly; wider ranges are one tap away. */
export const defaultFilters = () => ({
  preset: DEFAULT_PRESET,
  ...presetWindow(DEFAULT_PRESET),
  courier: '',
  account: '',
  routingMode: '',
  orderType: '',
  deliveryType: '',
})

/* Only the keys the API understands. The rail also carries `preset`, which is UI
   state — passing it into a query arg would fragment RTK Query's cache for no
   reason (two presets can resolve to the same window). */
export const apiFilters = (f = {}) => ({
  from: f.from,
  to: f.to,
  courier: f.courier,
  account: f.account,
  routingMode: f.routingMode,
  orderType: f.orderType,
  deliveryType: f.deliveryType,
})

const FIELD_H = 36

const fieldSx = {
  '& .MuiOutlinedInput-root': {height: FIELD_H, py: 0, borderRadius: '8px', fontSize: 13},
  '& .MuiOutlinedInput-root .MuiAutocomplete-input': {py: 0},
}

const dateFieldSx = {
  '& .MuiOutlinedInput-root': {height: FIELD_H, borderRadius: '8px', fontSize: 13, background: '#fff', py: 0, pr: '4px'},
  '& .MuiOutlinedInput-input': {fontSize: 13, py: 0, height: '100%', display: 'flex', alignItems: 'center'},
  '& .MuiOutlinedInput-notchedOutline': {borderColor: 'var(--border, #E5E7EB)'},
  '& .MuiOutlinedInput-notchedOutline legend': {display: 'none'},
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {borderColor: 'var(--primary, #6366F1)', borderWidth: 1},
  '& .MuiInputLabel-root': {display: 'none'},
  '& .MuiInputAdornment-root': {height: 'auto', margin: 0},
  '& .MuiIconButton-root': {padding: '4px', color: 'var(--neutral, #637381)'},
  '& .MuiSvgIcon-root': {fontSize: 18},
}

function Field({label, children, sx}) {
  return (
    <Box sx={{minWidth: 0, ...sx}}>
      <Typography
        sx={{fontSize: 10, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: 'text.secondary', mb: 0.5}}
      >
        {label}
      </Typography>
      {children}
    </Box>
  )
}
Field.propTypes = {label: PropTypes.node, children: PropTypes.node, sx: PropTypes.object}

/* Segmented preset selector — one tap for the ranges people actually pick. */
function PresetBar({value, onChange}) {
  return (
    <Box sx={{display: 'inline-flex', border: 1, borderColor: 'divider', borderRadius: '9px', overflow: 'hidden', bgcolor: 'background.paper'}}>
      {PRESETS.map((p) => {
        const active = value === p.key
        return (
          <Box
            key={p.key}
            component='button'
            type='button'
            onClick={() => onChange(p.key)}
            sx={{
              border: 0,
              cursor: 'pointer',
              px: 1.5,
              py: 0.85,
              fontSize: 11.5,
              fontWeight: 700,
              fontFamily: 'inherit',
              color: active ? '#fff' : 'text.secondary',
              bgcolor: active ? 'primary.main' : 'transparent',
              '&:hover': {bgcolor: active ? 'primary.main' : 'action.hover'},
            }}
          >
            {p.label}
          </Box>
        )
      })}
    </Box>
  )
}
PresetBar.propTypes = {value: PropTypes.string, onChange: PropTypes.func}

export default function DashboardFilters({filters, onChange, accounts, live, onLiveChange, updatedAt}) {
  const [draft, setDraft] = useState(filters)
  useEffect(() => setDraft(filters), [filters])
  const setD = (patch) => setDraft((d) => ({...d, ...patch}))

  const courierOptions = useMemo(
    () =>
      Array.from(new Set((accounts || []).map((a) => a.courier_code).filter(Boolean)))
        .sort()
        .map((c) => ({value: c, label: c})),
    [accounts]
  )

  /* Account options narrow to the chosen courier — with ~187 accounts the
     unfiltered list is unusable. */
  const accountOptions = useMemo(() => {
    const scoped = draft.courier ? (accounts || []).filter((a) => a.courier_code === draft.courier) : accounts || []
    return scoped.map((a) => ({value: a.account_code, label: a.display_name || a.account_code}))
  }, [accounts, draft.courier])

  const rangeStart = parse(draft.from)
  const rangeEnd = parse(draft.to)
  const defaultRange = rangeStart && rangeEnd ? [{startDate: rangeStart, endDate: rangeEnd, key: 'selection'}] : []

  const onPreset = (key) => {
    const next = {...draft, preset: key, ...presetWindow(key)}
    setDraft(next)
    onChange(next) // presets apply immediately — that's the point of a preset
  }

  const onRangeChange = (range) => {
    const sel = range && range[0]
    if (!sel) return
    setD({preset: 'custom', from: toApi(startOfDay(sel.startDate)), to: toApi(endOfDay(sel.endDate))})
  }

  const apply = () => onChange(draft)
  const reset = () => {
    const next = defaultFilters()
    setDraft(next)
    onChange(next)
  }

  const dirty = JSON.stringify(draft) !== JSON.stringify(filters)

  return (
    <Box sx={{border: 1, borderColor: 'divider', borderRadius: '14px', bgcolor: 'background.paper', p: 2, mb: 2.5, boxShadow: '0 1px 2px rgba(15,23,42,.04)'}}>
      <Stack direction='row' alignItems='center' flexWrap='wrap' gap={1.5} sx={{mb: 1.75}}>
        <PresetBar value={draft.preset} onChange={onPreset} />
        <Box sx={{width: 230, ...dateFieldSx}}>
          <DateRangePicker
            showTextBox
            size='small'
            label=''
            placeholder='Custom range'
            maxDate={new Date()}
            defaultRange={defaultRange}
            onChange={onRangeChange}
          />
        </Box>
        <Box sx={{flex: 1}} />
        <Stack direction='row' alignItems='center' spacing={0.5}>
          <Switch size='small' checked={!!live} onChange={(e) => onLiveChange(e.target.checked)} />
          <Typography sx={{fontSize: 12, fontWeight: 600, color: live ? 'success.main' : 'text.secondary'}}>
            {live ? 'Auto-refresh on' : 'Auto-refresh off'}
          </Typography>
          {updatedAt ? (
            <Typography sx={{fontSize: 11, color: 'text.disabled', ml: 0.5}}>· {updatedAt}</Typography>
          ) : null}
        </Stack>
      </Stack>

      <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(158px, 1fr))', gap: 1.5}}>
        <Field label='Courier'>
          <SearchSelect
            placeholder='All couriers'
            value={draft.courier || ''}
            onChange={(v) => setD({courier: v, account: ''})}
            options={courierOptions}
            sx={fieldSx}
          />
        </Field>
        <Field label='Account'>
          <SearchSelect
            placeholder='All accounts'
            value={draft.account || ''}
            onChange={(v) => setD({account: v})}
            options={accountOptions}
            sx={fieldSx}
          />
        </Field>
        <Field label='Routing'>
          <SearchSelect
            placeholder='All routing'
            value={draft.routingMode || ''}
            onChange={(v) => setD({routingMode: v})}
            options={[
              {value: 'NATIVE', label: 'PCIS native'},
              {value: 'CLICKPOST_FALLBACK', label: 'Clickpost fallback'},
            ]}
            sx={fieldSx}
          />
        </Field>
        <Field label='Delivery Type'>
          <SearchSelect
            placeholder='All delivery types'
            value={draft.deliveryType || ''}
            onChange={(v) => setD({deliveryType: v})}
            options={[
              {value: 'FORWARD', label: 'Forward'},
              {value: 'REVERSE', label: 'Reverse'},
            ]}
            sx={fieldSx}
          />
        </Field>
        <Field label='Payment'>
          <SearchSelect
            placeholder='All payment'
            value={draft.orderType || ''}
            onChange={(v) => setD({orderType: v})}
            options={[
              {value: 'COD', label: 'COD'},
              {value: 'PREPAID', label: 'Prepaid'},
            ]}
            sx={fieldSx}
          />
        </Field>
        <Field label='&nbsp;'>
          <Stack direction='row' spacing={1}>
            <Button
              fullWidth
              variant='contained'
              size='small'
              startIcon={<FilterAltOutlinedIcon />}
              onClick={apply}
              disabled={!dirty}
              sx={{height: FIELD_H, borderRadius: '9px', textTransform: 'none', fontWeight: 700}}
            >
              Apply
            </Button>
            <Button
              variant='outlined'
              size='small'
              onClick={reset}
              sx={{height: FIELD_H, minWidth: 40, px: 1, borderRadius: '9px', textTransform: 'none'}}
              title='Reset filters'
            >
              <RestartAltOutlinedIcon fontSize='small' />
            </Button>
          </Stack>
        </Field>
      </Box>
    </Box>
  )
}

DashboardFilters.propTypes = {
  filters: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  accounts: PropTypes.array,
  live: PropTypes.bool,
  onLiveChange: PropTypes.func.isRequired,
  updatedAt: PropTypes.string,
}
